import { Command } from "commander";
import { writeFileSync, mkdirSync, existsSync, readFileSync } from "node:fs";
import { basename, resolve } from "node:path";
import chalk from "chalk";

/**
 * Auto-detect project name from package.json or directory name.
 * Strips npm scope prefix if present (e.g., fathom-token-sdk -> sdk).
 */
export function detectProjectName(dir: string): string {
  try {
    const pkg = JSON.parse(readFileSync(resolve(dir, "package.json"), "utf-8"));
    if (pkg.name && typeof pkg.name === "string") {
      // Strip npm scope prefix: @scope/name -> name
      return pkg.name.replace(/^@[^/]+\//, "");
    }
  } catch {
    // No package.json or invalid JSON — use directory name
  }
  return basename(dir);
}

/**
 * Scaffold .fathom/ directory structure, config, registry, tracking.
 * Also scaffolds .claude/ skill + hooks for Claude Code users.
 * Reusable from both the setup command and the go command (auto-setup).
 * When `quiet` is true, only prints the summary line (used by go command).
 */
export function scaffoldProject(
  projectName: string,
  opts: { quiet?: boolean; projectDir?: string } = {},
): void {
  const baseDir = opts.projectDir ?? process.cwd();
  const log = opts.quiet
    ? () => {}
    : (msg: string) => console.log(msg);

  // 1. .fathom/ directories
  const fathomDir = resolve(baseDir, ".fathom");
  const trackingDir = resolve(fathomDir, "tracking", "sessions");
  const slicesDir = resolve(fathomDir, "slices");
  const storeDir = resolve(fathomDir, "store");
  mkdirSync(trackingDir, { recursive: true });
  mkdirSync(slicesDir, { recursive: true });
  mkdirSync(storeDir, { recursive: true });
  log(chalk.green("  ✓ .fathom/ directory structure"));

  // 2. Config
  const configPath = resolve(fathomDir, "config.json");
  if (!existsSync(configPath)) {
    writeFileSync(
      configPath,
      JSON.stringify(
        { project: projectName, storeType: "file", createdAt: new Date().toISOString() },
        null,
        2,
      ),
    );
    log(chalk.green("  ✓ config.json"));
  } else {
    log(chalk.dim("  · config.json (exists)"));
  }

  // 3. Registry (empty if none exists)
  const registryPath = resolve(fathomDir, "registry.json");
  if (!existsSync(registryPath)) {
    writeFileSync(
      registryPath,
      JSON.stringify(
        {
          project: projectName,
          features: [],
          generatedAt: new Date().toISOString(),
        },
        null,
        2,
      ),
    );
    log(chalk.green("  ✓ registry.json (empty)"));
  } else {
    log(chalk.dim("  · registry.json (exists)"));
  }

  // 4. Tracking summary
  const summaryPath = resolve(fathomDir, "tracking", "summary.json");
  if (!existsSync(summaryPath)) {
    writeFileSync(
      summaryPath,
      JSON.stringify(
        {
          project: projectName,
          sessions: [],
          lastUpdated: new Date().toISOString(),
        },
        null,
        2,
      ),
    );
    log(chalk.green("  ✓ tracking/summary.json"));
  } else {
    log(chalk.dim("  · tracking/summary.json (exists)"));
  }

  // 5. Claude Code skill (legacy support — will be replaced by projections once intent exists)
  const skillDir = resolve(baseDir, ".claude", "skills", "fathom");
  mkdirSync(skillDir, { recursive: true });

  const skillPath = resolve(skillDir, "SKILL.md");
  if (!existsSync(skillPath)) {
    const skillContent = generateSkill(projectName);
    writeFileSync(skillPath, skillContent);
    log(chalk.green("  ✓ .claude/skills/fathom/SKILL.md"));
  } else {
    log(chalk.dim("  · .claude/skills/fathom/SKILL.md (exists)"));
  }

  // 6. Post-session hook for auto-tracking
  installPostSessionHook(baseDir, log);

  // 7. .mcp.json for MCP auto-discovery
  const mcpPath = resolve(baseDir, ".mcp.json");
  let mcpConfig: Record<string, unknown> = { mcpServers: {} };
  if (existsSync(mcpPath)) {
    try {
      mcpConfig = JSON.parse(readFileSync(mcpPath, "utf-8"));
      if (!mcpConfig.mcpServers) mcpConfig.mcpServers = {};
    } catch {
      // Invalid JSON — create fresh
      mcpConfig = { mcpServers: {} };
    }
  }
  (mcpConfig.mcpServers as Record<string, unknown>).fathom = {
    command: "npx",
    args: ["-y", "fathom-token-mcp@latest", "--project-dir", "."],
  };
  writeFileSync(mcpPath, JSON.stringify(mcpConfig, null, 2) + "\n");
  log(chalk.green("  ✓ .mcp.json (MCP auto-discovery)"));
}

/**
 * Auto-detect tech stack from filesystem markers and package.json dependencies.
 * Returns a sorted, deduplicated array of technology identifiers.
 */
export function detectStack(dir: string): string[] {
  const stack = new Set<string>();

  // File-based detection
  const fileMarkers: Array<[string | RegExp, string]> = [
    ["package.json", "node"],
    ["tsconfig.json", "typescript"],
    ["Cargo.toml", "rust"],
    ["go.mod", "go"],
    ["requirements.txt", "python"],
    ["pyproject.toml", "python"],
    ["Gemfile", "ruby"],
  ];

  for (const [marker, tech] of fileMarkers) {
    if (typeof marker === "string" && existsSync(resolve(dir, marker))) {
      stack.add(tech);
    }
  }

  // Glob-like file detection for config files with variable extensions
  const globMarkers: Array<[string, string]> = [
    ["next.config", "next"],
    ["vite.config", "vite"],
  ];

  try {
    const files = require("node:fs").readdirSync(dir) as string[];
    for (const [prefix, tech] of globMarkers) {
      if (files.some((f: string) => f.startsWith(prefix))) {
        stack.add(tech);
      }
    }
  } catch {
    // Directory not readable
  }

  // Parse package.json dependencies for known frameworks/libraries
  const depMarkers = [
    "react", "vue", "svelte", "@angular/core",
    "express", "fastify", "hono",
    "prisma", "drizzle-orm",
  ];

  try {
    const pkg = JSON.parse(readFileSync(resolve(dir, "package.json"), "utf-8"));
    const allDeps = {
      ...((pkg.dependencies as Record<string, string>) ?? {}),
      ...((pkg.devDependencies as Record<string, string>) ?? {}),
    };

    for (const dep of depMarkers) {
      if (dep in allDeps) {
        // Use short name: @angular/core -> angular, drizzle-orm -> drizzle
        const shortName = dep.replace(/^@/, "").replace(/\/core$/, "").replace(/-orm$/, "");
        stack.add(shortName);
      }
    }
  } catch {
    // No package.json or invalid JSON
  }

  return [...stack].sort();
}

export const setupCommand = new Command("setup")
  .description("Scaffold .fathom/ project directory + .claude/ skill + hooks")
  .option("-p, --project <name>", "Project name")
  .action((options: { project?: string }) => {
    const projectName = options.project ?? detectProjectName(process.cwd());

    console.log(chalk.bold(`\nFathom — Setup: ${projectName}`));
    console.log(chalk.dim("─".repeat(50)));

    scaffoldProject(projectName);

    console.log(chalk.bold(`\n✓ Setup complete for "${projectName}"`));
    console.log(chalk.dim("  Next: run `fathom` to capture project intent and start working"));
    console.log(chalk.dim("  Then: git add .fathom .claude && git commit\n"));
  });

function generateSkill(projectName: string): string {
  return `---
name: Fathom Tracking
description: Feature-level session tracking for ${projectName}
---

# Fathom — Feature Tracking

This project uses Fathom for token estimation, session tracking, and velocity measurement.

## Feature Registry

Read \`.fathom/registry.json\` at session start to identify which feature this session is working on.

## Project Intent

If \`.fathom/intent.yaml\` exists, read it to understand the project's purpose, values, and guardrails.

## How to Help

1. **Reference feature IDs** — When working on a feature, mention its ID (e.g., \`[auth-001]\`) in your responses so the auto-tagger can match sessions to features.
2. **Stay focused** — Work on one feature per session when possible. Multi-feature sessions are harder to tag accurately.
3. **Use branch conventions** — Feature branches should match patterns in the registry (e.g., \`feat/auth-login\`).

## Tracking Summary

Check \`.fathom/tracking/summary.json\` for aggregated session data across features.
`;
}

/**
 * Install Claude Code post-session hook for automatic session tracking.
 * Creates .claude/hooks/post-session.sh that runs `fathom track --yes`.
 * No-op if hook already contains `fathom track`.
 * Only installs if .claude/ directory exists (indicating Claude Code project).
 */
export function installPostSessionHook(
  baseDir: string,
  log: (msg: string) => void = () => {},
): boolean {
  const claudeDir = resolve(baseDir, ".claude");

  // Only install if .claude/ exists (Claude Code project)
  if (!existsSync(claudeDir)) {
    return false;
  }

  const hooksDir = resolve(claudeDir, "hooks");
  mkdirSync(hooksDir, { recursive: true });

  const hookPath = resolve(hooksDir, "post-session.sh");

  if (existsSync(hookPath)) {
    // Check if it already has fathom track
    const content = readFileSync(hookPath, "utf-8");
    if (content.includes("fathom track")) {
      log(chalk.dim("  · .claude/hooks/post-session.sh (exists)"));
      return false;
    }
    // Append fathom track to existing hook
    const appended = content.trimEnd() + "\nfathom track --yes 2>/dev/null || true\n";
    writeFileSync(hookPath, appended, { mode: 0o755 });
    log(chalk.green("  + Claude Code post-session hook (appended)"));
    return true;
  }

  // Create new hook
  writeFileSync(hookPath, generateHookScript(""), { mode: 0o755 });
  log(chalk.green("  + Claude Code post-session hook"));
  return true;
}

function generateHookScript(_projectName: string): string {
  return `#!/bin/sh
# Fathom auto-track: import sessions after each Claude Code conversation
# Installed by fathom init / fathom_init_project MCP tool
# Non-blocking: failures do not affect Claude Code
fathom track --yes 2>/dev/null || true
`;
}
